/* istanbul ignore file */
import React, { useState, useEffect } from 'react'
import { string, shape, func } from 'prop-types'
import Spinner from '@divvy-web/skylab.spinner'
import Modal from '@divvy-web/skylab.modal'
import { v4 as uuidv4 } from 'uuid'
import { css } from '@emotion/core'
import { FM } from '@divvy-web/i18n'
import { useMXWidget } from '../useMXWidget'
import { BANK_ATRIUM } from '../../constants'
import CreateBankMemberMutation from './CreateBankMemberMutation.gql'
import CheckForNewBankMembersQuery from './CheckForNewBankMembersQuery.gql'

/****************************************************************
MX/Atrium

To test a broken connection, First connect "mx bank" and use the following creds:
username: test_atrium
password: UNAUTHORIZED

Then, run `iex -S mix` in viking phoenix-api directory and then inside the console, run:
ZB.Jobs.Banks.CheckForNewMembers.perform(68)

You may need to change account id. I log in as "dv+tadm@getdivvy.com" and that
seems to map to 68 in viking.

Now mx bank should load with a connection error

To clear out bank accounts, run `mix clean.banks` in viking
*****************************************************************/
const MXWidget = ({ client, memberToFix, onConnectedSuccess, onFailedToConnect }) => {
  // mx uses an anchor id to mount the widget
  const [anchorId] = useState(uuidv4())

  /*
  * This is once the widget has validated their login and they have selected accounts
  * example success response:
  * {
  *   member_guid: "MBR-dfe9a958-23d3-4ab0-8642-7e3a9ff3cff5"
  *   user_guid: "USR-2f18b67b-32d6-4456-9c9a-a08814126a13"
  * }
  */
  const onSuccess = ({ member_guid: token }) => {
    client.mutate({
      mutation: CreateBankMemberMutation,
      variables: { input: { externalId: token, provider: BANK_ATRIUM } }
    }).then(() => {
      // give the widget a little time to finish
      setTimeout(() => {
        onConnectedSuccess()
      }, 200)
    })
  }

  // when the close button on the Skylab Modal is clicked
  const onCloseClick = () => {
    onFailedToConnect(null, BANK_ATRIUM)
    client.query({
      fetchPolicy: 'no-cache',
      query: CheckForNewBankMembersQuery,
      variables: {}
    })
  }

  // config for atrium widget
  // https://atrium.mx.com/docs#mx-connect-widget
  const atriumConfig = {
    anchorId,
    client,
    onExit: () => onFailedToConnect(null, BANK_ATRIUM),
    onSuccess
  }

  // https://atrium.mx.com/docs#configuration-options
  if (memberToFix) {
    atriumConfig.config = { current_member_guid: memberToFix.externalId }
  }

  const { error, open, ready } = useMXWidget(atriumConfig)

  // we translate being in the dom as being open even though the different widgets handle open/close differently,
  // so we handle that in useEffect
  useEffect(() => {
    if (error) return
    if (!ready) return

    open()

    // with MX, sometimes the widget can connect a bank on their servers,
    // but theres no webhooks or anything to let us know, so if the page is
    // refreshed or closed early before we can notify our backend, then we
    // dont' know to fetch the account. To remedy, we call this when the widget
    // is opened and also closed to be thorough that our backedn checks for banks.
    // If they connected a bank and we failed to know about it and fetch it, the
    // natural thing for them to do is open the widget again, so thats why we
    // try to fetch it because then the bank will appear and they'll close
    // the widget.
    client.query({
      fetchPolicy: 'no-cache',
      query: CheckForNewBankMembersQuery,
      variables: {}
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, ready])

  let content
  if (!ready) {
    content = <Spinner centered />
  } else if (error) {
    content = <FM msg='We are unable to open this bank connection widget' />
  } else {
    content = <div css={widgetCss} id={anchorId} />
  }

  return (
    <Modal isShowing onCloseClick={onCloseClick}>
      {content}
    </Modal>
  )
}

const widgetCss = css`
  width: 400px;
`

MXWidget.propTypes = {
  client: shape({
    query: func
  }).isRequired,
  memberToFix: shape({
    id: string
  }),
  onConnectedSuccess: func.isRequired,
  onFailedToConnect: func.isRequired
}

export default MXWidget
